import { gql } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { apolloClient } from 'core/apollo-client';

// GetDocsLambda checks the caller may see the account/offer the document belongs to, then signs a
// short-lived link. staff-fe used to sign S3 URLs itself with the identity-pool credentials, which
// gave every staff and agent session read access to the whole bucket (#36196).
const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($path: String!, $accountId: ID, $offerId: ID) {
    getDocumentDownloadUrl(path: $path, accountId: $accountId, offerId: $offerId) {
      url
    }
  }
`;

/**
 * Returns a short-lived signed URL for a policy-docs key (or an `s3://` URL for that bucket).
 * Pass the accountId when the document is shown on an account: agents need it to open member task
 * uploads (`uploads/...`), whose key names no account. Pass the offerId on an offer: agents need it
 * for migrated farm offers, which the API can't find by webUserId.
 */
export async function getDocumentDownloadUrl(path, { accountId = null, offerId = null } = {}) {
  const { data } = await apolloClient.query({
    query: GET_DOCUMENT_DOWNLOAD_URL,
    variables: { path, accountId, offerId },
    fetchPolicy: 'no-cache'
  });
  const url = data?.getDocumentDownloadUrl?.url;
  if (!url) {
    throw new Error('No download URL returned');
  }
  return url;
}

export async function openDocument({ path, accountId, offerId, flow }) {
  try {
    window.open(await getDocumentDownloadUrl(path, { accountId, offerId }), '_blank');
  } catch (error) {
    // Report, don't just log: a swallowed error here is how the v3 presigner outage (#33733)
    // produced zero Sentry events while every document click was broken for ~a day.
    console.error('Error downloading document:', error);
    Sentry.captureException(error, { tags: { flow }, extra: { path } });
  }
}
